import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { colors, font } from '../../lib/tokens';
import { EHR_CASES, EhrCase } from '../../data/ehrCases';

type Phase = 'typing' | 'suggestions' | 'confirming' | 'confirmed';

// Drives the note typewriter. Each run is tagged with `runKey`: bumping it
// (next case, loop iteration, or a pause) rewinds the note to empty, and
// `done` is only ever true for the current run, so the suggestion sequence
// can't fire off the previous run's finished text. `instant` skips the
// typing (reduced motion).
function useTypedNote(
  fullText: string,
  runKey: number,
  running: boolean,
  instant: boolean,
  startDelay = 300,
  cps = 170
) {
  const [typed, setTyped] = useState({ key: runKey, n: 0 });

  useEffect(() => {
    if (!running) return;
    if (instant) {
      setTyped({ key: runKey, n: fullText.length });
      return;
    }
    let raf = 0;
    const start = performance.now() + startDelay;
    const tick = (now: number) => {
      const n = Math.min(fullText.length, Math.floor((Math.max(0, now - start) * cps) / 1000));
      setTyped((t) => (t.key === runKey && t.n === n ? t : { key: runKey, n }));
      if (n < fullText.length) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [fullText, runKey, running, instant, startDelay, cps]);

  const n = typed.key === runKey ? typed.n : 0;
  return { visible: fullText.slice(0, n), done: n >= fullText.length };
}

function usePrefersReducedMotion() {
  const query = '(prefers-reduced-motion: reduce)';
  const [reduce, setReduce] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setReduce(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduce;
}

// True while the element is on screen and the tab is visible — the demo only
// animates then, so it doesn't spend a phone's battery off screen.
function useOnScreen(ref: React.RefObject<HTMLElement>) {
  const [onScreen, setOnScreen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || !('IntersectionObserver' in window)) {
      setOnScreen(true);
      return;
    }
    let inView = false;
    const update = () => setOnScreen(inView && !document.hidden);
    const io = new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting;
      update();
    });
    io.observe(el);
    document.addEventListener('visibilitychange', update);
    return () => {
      io.disconnect();
      document.removeEventListener('visibilitychange', update);
    };
  }, [ref]);
  return onScreen;
}

type EhrMockProps = {
  compact?: boolean;
  loop?: boolean;
  cases?: EhrCase[];
};

export default function EhrMock({ compact = false, loop = true, cases = EHR_CASES }: EhrMockProps) {
  const [caseIdx, setCaseIdx] = useState(0);
  const [loopKey, setLoopKey] = useState(0);
  const [phase, setPhase] = useState<Phase>('typing');
  const [revealN, setRevealN] = useState(0);
  const [confirmedIdx, setConfirmedIdx] = useState<number | null>(null);

  const activeCase = cases[caseIdx];
  const noteText = activeCase.noteLines.join('\n');
  const primaryIdx = Math.max(0, activeCase.suggestions.findIndex((s) => s.primary));
  const primary = activeCase.suggestions[primaryIdx];

  const rootRef = useRef<HTMLDivElement>(null);
  const noteRef = useRef<HTMLDivElement>(null);
  const running = useOnScreen(rootRef);
  const reduceMotion = usePrefersReducedMotion();
  const looping = loop && !reduceMotion;

  const { visible: displayed, done: typingDone } = useTypedNote(noteText, loopKey, running, reduceMotion);

  // Paused (scrolled away or tab hidden): rewind the current case so it
  // replays from an empty note the next time the demo is on screen.
  useEffect(() => {
    if (running) return;
    setPhase('typing');
    setRevealN(0);
    setConfirmedIdx(null);
    setLoopKey((k) => k + 1);
  }, [running]);

  // Keep the newest dictated line in view when the note outgrows its box
  // (the phone layout gives the note less height than the longest cases need).
  useLayoutEffect(() => {
    const el = noteRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [displayed]);

  useEffect(() => {
    if (!typingDone || !running) return;
    setPhase('suggestions');
    const timers: ReturnType<typeof setTimeout>[] = [];
    const n = activeCase.suggestions.length;

    activeCase.suggestions.forEach((_, i) => {
      timers.push(setTimeout(() => setRevealN(i + 1), 250 + i * 220));
    });

    timers.push(
      setTimeout(() => {
        setPhase('confirming');
        setConfirmedIdx(primaryIdx);
      }, 250 + n * 220 + 600)
    );
    timers.push(
      setTimeout(() => setPhase('confirmed'), 250 + n * 220 + 1400)
    );

    if (looping) {
      timers.push(
        setTimeout(() => {
          setPhase('typing');
          setRevealN(0);
          setConfirmedIdx(null);
          setCaseIdx((c) => (c + 1) % cases.length);
          setLoopKey((k) => k + 1);
        }, 250 + n * 220 + 5500)
      );
    }
    return () => timers.forEach(clearTimeout);
    // primaryIdx / activeCase depend on caseIdx + cases; re-run when typing flips done.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typingDone, running, looping, caseIdx]);

  const accent = colors.blue;
  const accentBg = colors.blueSoft;
  const accentBorder = '#BFDBFE';

  return (
    <div
      ref={rootRef}
      className="ehr-mock"
      style={{
        fontFamily: font.sans,
        color: colors.ink,
        background: colors.surface,
        border: `1px solid ${colors.hair}`,
        borderRadius: 12,
        boxShadow: '0 1px 2px rgba(16,24,40,0.04), 0 12px 40px rgba(16,24,40,0.08)',
        overflow: 'hidden',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Window chrome */}
      <div
        style={{
          height: 36,
          background: '#F8FAFC',
          borderBottom: `1px solid ${colors.hair}`,
          display: 'flex',
          alignItems: 'center',
          padding: '0 14px',
          gap: 8,
          flexShrink: 0,
        }}
      >
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#EF4444' }} />
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#F59E0B' }} />
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#10B981' }} />
        <div style={{ flex: 1, textAlign: 'center', fontSize: 12, color: colors.muted, fontWeight: 500 }}>
          Outpatient Record · {activeCase.patient}
        </div>
      </div>

      <div className="ehr-mock-body">
        {/* LEFT: note + diagnoses */}
        <div
          className="ehr-mock-left"
          style={{
            padding: compact ? 18 : 24,
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}
        >
          <div
            className="ehr-mock-meta"
            style={{ display: 'grid', gridTemplateColumns: '110px 1fr', rowGap: 6, columnGap: 12, fontSize: 12 }}
          >
            <span style={{ color: colors.muted }}>Encounter</span>
            <span style={{ color: colors.ink }}>{activeCase.encounter}</span>
            <span style={{ color: colors.muted }}>Department</span>
            <span style={{ color: colors.ink }}>{activeCase.department}</span>
            <span style={{ color: colors.muted }}>Template</span>
            <span style={{ color: colors.ink }}>{activeCase.template}</span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minHeight: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: 0.6,
                  textTransform: 'uppercase',
                  color: colors.muted,
                }}
              >
                Clinical note
              </span>
              <span style={{ fontSize: 10, color: colors.dim, fontFamily: font.mono }}>
                {typingDone ? 'saved' : 'dictating…'}
              </span>
            </div>
            <div
              ref={noteRef}
              className="ehr-note-body"
              style={{
                background: '#F8FAFC',
                border: `1px solid ${colors.hair}`,
                borderRadius: 8,
                padding: 14,
                fontFamily: font.mono,
                fontSize: 11.5,
                lineHeight: 1.65,
                color: colors.ink2,
                whiteSpace: 'pre-wrap',
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              {displayed}
              {!typingDone && (
                <span
                  style={{
                    display: 'inline-block',
                    width: 6,
                    height: 13,
                    background: accent,
                    verticalAlign: 'text-bottom',
                    marginLeft: 2,
                    animation: 'ehr-caret 1s step-end infinite',
                  }}
                />
              )}
            </div>
          </div>

          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: 0.6,
                textTransform: 'uppercase',
                color: colors.muted,
                marginBottom: 8,
              }}
            >
              Diagnoses
            </div>
            <div
              style={{
                border: `1px dashed ${colors.hair}`,
                borderRadius: 8,
                padding: 10,
                minHeight: 44,
                display: 'flex',
                gap: 8,
                flexWrap: 'wrap',
                background: phase === 'confirmed' ? '#F0FDF4' : colors.surface,
                borderStyle: phase === 'confirmed' ? 'solid' : 'dashed',
                borderColor: phase === 'confirmed' ? '#BBF7D0' : colors.hair,
                transition: 'all 400ms ease',
              }}
            >
              {phase === 'confirmed' ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 10px',
                    background: colors.surface,
                    border: '1px solid #BBF7D0',
                    borderRadius: 6,
                    fontSize: 12,
                    animation: 'ehr-pop 400ms ease',
                  }}
                >
                  <span style={{ width: 4, height: 16, background: '#16A34A', borderRadius: 2 }} />
                  <span style={{ fontFamily: font.mono, fontWeight: 600 }}>{primary.code}</span>
                  <span style={{ color: '#334155' }}>{primary.desc}</span>
                  <svg width="12" height="12" viewBox="0 0 12 12" style={{ marginLeft: 4 }}>
                    <path
                      d="M2 6l3 3 5-6"
                      stroke="#16A34A"
                      strokeWidth="2"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              ) : (
                <span style={{ fontSize: 11, color: colors.dim, fontStyle: 'italic', alignSelf: 'center' }}>
                  {phase === 'typing' ? 'awaiting clinical note…' : 'review suggestions →'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT: AI suggestions panel */}
        <div
          className="ehr-mock-right"
          style={{
            padding: compact ? 16 : 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            background: '#FCFCFD',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: accent,
                  boxShadow: `0 0 0 4px ${accentBg}`,
                  animation: phase === 'typing' ? 'ehr-pulse 1.6s ease-in-out infinite' : 'none',
                }}
              />
              <span style={{ fontSize: 13, fontWeight: 600 }}>ICD-10 Suggestions</span>
            </div>
            <span style={{ fontFamily: font.mono, fontSize: 10, color: colors.dim }}>
              {phase === 'typing' ? 'listening' : `${revealN} found`}
            </span>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '64px 1fr 44px',
              fontSize: 10,
              letterSpacing: 0.5,
              textTransform: 'uppercase',
              color: colors.dim,
              paddingBottom: 6,
              borderBottom: `1px solid ${colors.hair2}`,
              gap: 8,
            }}
          >
            <span>Code</span>
            <span>Description</span>
            <span style={{ textAlign: 'right' }}>Conf.</span>
          </div>

          <div
            className="ehr-suggestions-list"
            style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, overflow: 'hidden' }}
          >
            {activeCase.suggestions.map((s, i) => {
              const visible = i < revealN;
              const isPrimary = !!s.primary;
              const isConfirming = phase === 'confirming' && i === confirmedIdx;
              const isConfirmed = phase === 'confirmed' && i === confirmedIdx;
              return (
                <div
                  key={s.code + i}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '64px 1fr 44px',
                    gap: 8,
                    alignItems: 'center',
                    padding: '8px 10px',
                    borderRadius: 6,
                    border: isPrimary ? `1px solid ${accentBorder}` : '1px solid transparent',
                    background: isConfirming
                      ? accentBg
                      : isConfirmed
                      ? '#F0FDF4'
                      : isPrimary
                      ? accentBg
                      : 'transparent',
                    opacity: visible ? 1 : 0,
                    transform: visible ? 'translateY(0)' : 'translateY(6px)',
                    transition: 'opacity 240ms ease, transform 240ms ease, background 300ms ease',
                    fontSize: 11,
                  }}
                >
                  <span style={{ fontFamily: font.mono, fontWeight: 600, color: isPrimary ? accent : colors.ink }}>
                    {s.code}
                  </span>
                  <span
                    style={{
                      color: '#334155',
                      fontWeight: isPrimary ? 600 : 400,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {s.desc}
                  </span>
                  <span
                    style={{
                      fontFamily: font.mono,
                      fontSize: 10,
                      textAlign: 'right',
                      color: isPrimary ? accent : colors.dim,
                    }}
                  >
                    {Math.round(s.conf * 100)}%
                  </span>
                </div>
              );
            })}
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              paddingTop: 10,
              borderTop: `1px solid ${colors.hair2}`,
              fontSize: 11,
              color: colors.muted,
            }}
          >
            <span
              style={{
                width: 5,
                height: 5,
                borderRadius: '50%',
                background: phase === 'confirmed' ? '#16A34A' : accent,
              }}
            />
            <span style={{ fontFamily: font.mono, fontSize: 10 }}>
              {phase === 'typing' && 'analyzing…'}
              {phase === 'suggestions' && 'ranked by confidence'}
              {phase === 'confirming' && `confirming ${primary.code}…`}
              {phase === 'confirmed' && 'pushed to patient record'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
